const TOAST_VISIBLE_DURATION = 10000
const TOAST_TRANSITION_DURATION = 220
const DEFAULT_MESSAGE = 'Dodano do koszyka'
const DEFAULT_CTA_TEXT = 'Przejdz do koszyka'
const DEFAULT_PRODUCT_TITLE = ''

class CartNotificationComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #toast = null

  /** @type {HTMLElement | null} */
  #message = null

  /** @type {HTMLImageElement | null} */
  #image = null

  /** @type {HTMLElement | null} */
  #imageWrap = null

  /** @type {HTMLElement | null} */
  #productTitle = null

  /** @type {HTMLButtonElement | null} */
  #cta = null

  /** @type {HTMLButtonElement | null} */
  #closeButton = null

  /** @type {number | undefined} */
  #hideTimeout

  /** @type {number | undefined} */
  #cleanupTimeout

  connectedCallback() {
    if (!this.hasChildNodes()) {
      this.innerHTML = `
        <div class="cart-notification__toast" ref="toast" role="status" aria-live="polite" aria-atomic="true">
          <button type="button" class="cart-notification__close" ref="closeButton" aria-label="${Theme?.translations?.close || 'Close'}">&times;</button>
          <div class="cart-notification__layout">
            <div class="cart-notification__image-wrap" ref="imageWrap">
              <img class="cart-notification__image" ref="image" alt="" loading="lazy">
            </div>
            <div class="cart-notification__content">
              <p class="cart-notification__message" ref="message"></p>
              <p class="cart-notification__product-title" ref="productTitle"></p>
              <button type="button" class="cart-notification__cta" ref="cta">
                ${Theme?.translations?.view_cart || DEFAULT_CTA_TEXT}
              </button>
            </div>
          </div>
        </div>
      `
    }

    this.#toast = this.querySelector('[ref="toast"]')
    this.#message = this.querySelector('[ref="message"]')
    this.#image = this.querySelector('[ref="image"]')
    this.#imageWrap = this.querySelector('[ref="imageWrap"]')
    this.#productTitle = this.querySelector('[ref="productTitle"]')
    this.#cta = this.querySelector('[ref="cta"]')
    this.#closeButton = this.querySelector('[ref="closeButton"]')

    this.#closeButton?.addEventListener('click', this.#handleCloseClick)
    this.#cta?.addEventListener('click', this.#handleCtaClick)
    document.addEventListener('cart-notification:show', this.#handleManualShow)
  }

  disconnectedCallback() {
    this.#closeButton?.removeEventListener('click', this.#handleCloseClick)
    this.#cta?.removeEventListener('click', this.#handleCtaClick)
    document.removeEventListener('cart-notification:show', this.#handleManualShow)
    if (this.#hideTimeout) clearTimeout(this.#hideTimeout)
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout)
  }

  /**
   * @param {CustomEvent} event
   */
  #handleManualShow = (event) => {
    const detail = event?.detail || {}
    this.#show({
      message: this.#buildMessage(detail.itemCount),
      productTitle: detail.productTitle || DEFAULT_PRODUCT_TITLE,
      productImage: detail.productImage || '',
      productUrl: detail.productUrl || Theme?.routes?.cart_url || '/cart'
    })
  }

  #handleCloseClick = () => {
    this.#hide()
  }

  #handleCtaClick = () => {
    const cartDrawer = document.querySelector('cart-drawer-component')
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open()
      this.#hide()
      return
    }

    window.location.href = Theme?.routes?.cart_url || '/cart'
  }

  /**
   * @param {number | string | undefined} itemCount
   * @returns {string}
   */
  #buildMessage(itemCount) {
    const count = Number(itemCount)
    const oneItemText = Theme?.translations?.items_added_to_cart_one || DEFAULT_MESSAGE
    const manyItemsText = Theme?.translations?.items_added_to_cart_other || DEFAULT_MESSAGE
    const fallbackText = Theme?.translations?.added_to_cart || DEFAULT_MESSAGE

    if (!Number.isFinite(count) || count <= 0) {
      return fallbackText
    }

    if (count === 1) {
      return oneItemText
    }

    return manyItemsText.includes('{{ count }}')
      ? manyItemsText.replace('{{ count }}', count.toString())
      : manyItemsText
  }

  /**
   * @param {{message: string, productTitle: string, productImage: string, productUrl: string}} payload
   */
  #show(payload) {
    if (!this.#toast || !this.#message || !this.#productTitle || !this.#cta) return

    this.#message.textContent = payload.message
    this.#productTitle.textContent = payload.productTitle

    if (this.#image && this.#imageWrap) {
      if (payload.productImage) {
        this.#image.src = payload.productImage
        this.#image.alt = payload.productTitle || ''
        this.#imageWrap.classList.remove('hidden')
      } else {
        this.#image.removeAttribute('src')
        this.#image.alt = ''
        this.#imageWrap.classList.add('hidden')
      }
    }

    if (this.#hideTimeout) clearTimeout(this.#hideTimeout)
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout)

    this.classList.add('is-visible')
    this.#toast.classList.add('is-visible')

    this.#hideTimeout = window.setTimeout(() => {
      this.#hide()
    }, TOAST_VISIBLE_DURATION)
  }

  #hide() {
    this.#toast?.classList.remove('is-visible')
    this.#cleanupTimeout = window.setTimeout(() => {
      this.classList.remove('is-visible')
    }, TOAST_TRANSITION_DURATION)
  }
}

if (!customElements.get('cart-notification-component')) {
  customElements.define('cart-notification-component', CartNotificationComponent)
}

const mountCartNotification = () => {
  if (document.querySelector('cart-notification-component')) return
  if (!document.body) return

  const notification = document.createElement('cart-notification-component')
  notification.classList.add('cart-notification')
  document.body.appendChild(notification)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountCartNotification, { once: true })
} else {
  mountCartNotification()
}
